import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["progressBar", "progressText", "resultsContainer", "errorContainer"]
  static values = {
    url: String,
    interval: { type: Number, default: 3000 }
  }

  connect() {
    this.retryCount = 0
    this.maxRetries = 10
    this.lastProcessedCount = 0
    this.lastTotalCount = 0
    this.lastStatus = null
    this.hideError()
    this.poll()
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  async poll() {
    try {
      const response = await fetch(this.urlValue, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()

      if (data.error) {
        let errorMsg = data.error;
        if (typeof errorMsg === 'object' && errorMsg !== null && errorMsg.message) {
          errorMsg = errorMsg.message;
        }
        console.log("🟠 AiComparisonPollingController - Error received from server:", data.error);
        console.log("🟠 AiComparisonPollingController - Error type:", typeof data.error);
        console.log("🟠 AiComparisonPollingController - Error message:", errorMsg);
        this.showError(data.error);
      } else {
        this.hideError();
      }

      if (data.status === 'not_found') {
        this.retryCount++
        
        if (this.retryCount >= this.maxRetries) {
          this.disconnect()
          return
        } else {
          this.timeout = setTimeout(() => this.poll(), this.intervalValue)
          return
        }
      }

      this.retryCount = 0

      const hasProgressRegressed = data.processed < this.lastProcessedCount
      const hasTotalChanged = data.total !== this.lastTotalCount && this.lastTotalCount > 0
      const hasStatusRegressed = this.lastStatus === 'processing' && data.status === 'initializing'

      if ((hasProgressRegressed && !hasTotalChanged) || hasStatusRegressed) {
        const regressionType = hasStatusRegressed ? "status" : "progress"
        if (data.status === 'completed') {
          const url = new URL(window.location.href)
          const jobId = new URLSearchParams(this.urlValue).get('job_id')
          url.searchParams.set('job_id', jobId)
          window.history.pushState({}, '', url.toString())
        } else if (data.status === 'initializing' || data.processed < data.total) {
          this.timeout = setTimeout(() => this.poll(), this.intervalValue)
        }
        return
      }

      const percentage = (data.processed / data.total) * 100
      this.lastProcessedCount = Math.max(this.lastProcessedCount, data.processed)
      this.lastTotalCount = data.total
      this.lastStatus = data.status
      
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = `${percentage}%`
      }
      
      if (this.hasProgressTextTarget) {
        const statusText = this.getStatusText(data.status)
        const progressText = `${statusText} (${data.processed}/${data.total})`
        this.progressTextTarget.textContent = progressText
      }

      if (this.hasResultsContainerTarget) {
        this.updateResults(data.results || [])
      }

      if (data.status === 'completed') {
        const exportSection = document.getElementById('export-results-section')
        if (exportSection) {
          exportSection.style.display = 'block'
          const exportButton = document.getElementById('export-current-data-button')
          if (exportButton) {
            const jobId = new URLSearchParams(this.urlValue).get('job_id')
            const currentHref = exportButton.getAttribute('href')
            const url = new URL(currentHref, window.location.origin)
            url.searchParams.set('job_id', jobId)
            exportButton.setAttribute('href', url.toString())
          }
        }
      } else if (data.status === 'initializing' || data.processed < data.total) {
        this.timeout = setTimeout(() => this.poll(), this.intervalValue)
      }
    } catch (error) {
      this.showError('Error while retrieving results : ' + error.message)
      this.timeout = setTimeout(() => this.poll(), this.intervalValue)
    }
  }

  updateResults(results) {
    if (!this.hasResultsContainerTarget) {
      return
    }

    const tableHTML = `
      <table class="comparison-table">
        <thead>
          <tr>
            <th>Order</th>
            <th>Photo</th>
            <th>Description</th>
            <th>Dimensions</th>
            <th>Number of Pieces</th>
            <th>Category</th>
            <th>Tags</th>
            <th>Volume</th>
            <th>Packed Volume (Prod)</th>
            <th>Real Volume (Prod)</th>
            <th>Ratio volume prod</th>
            <th>Ratio volume local</th>
          </tr>
        </thead>
        <tbody>
          ${results.length > 0 ? results.map((result, index) => {
            return this.generateTableRow(result)
          }).join('') : '<tr><td colspan="12" style="text-align: center; padding: 20px;">No results yet...</td></tr>'}
        </tbody>
      </table>
    `

    this.resultsContainerTarget.innerHTML = tableHTML
  }

  generateTableRow(result) {
    const dimsEqual = result.current_dimensions.length == result.ai_dimensions.length &&
      result.current_dimensions.height == result.ai_dimensions.height &&
      result.current_dimensions.depth == result.ai_dimensions.depth;
    const nbEqual = result.nb_of_pieces == result.ai_nb_of_pieces;
    const catEqual = result.category == result.ai_category;
    const tagsEqual = JSON.stringify(result.tags) == JSON.stringify(result.ai_tags);
    const aiDimsClass = dimsEqual ? 'ai-equal' : 'ai-different';
    const aiNbClass = nbEqual ? 'ai-equal' : 'ai-different';
    const aiCatClass = catEqual ? 'ai-equal' : 'ai-different';
    const aiTagsClass = tagsEqual ? 'ai-equal' : 'ai-different';
    const currentTags = result.tags ? result.tags.map(tag => `<span class="tag">${tag}</span>`).join('') : '';
    const aiTags = result.ai_tags ? result.ai_tags.map(tag => `<span class="tag">${tag}</span>`).join('') : currentTags;

    function ceilOrNA(val) {
      const result = (val === undefined || val === null || isNaN(val)) ? 'N/A' : Math.ceil(Number(val));
      return result;
    }

    function formatVolume(val) {
      const result = (val === undefined || val === null || isNaN(val)) ? 'N/A' : Number(val).toFixed(6);
      return result;
    }

    function formatRatio(val) {
      const result = (val === undefined || val === null || isNaN(val)) ? 'N/A' : Number(val).toFixed(3);
      return result;
    }

    const rowHTML = `
      <tr>
        <td><a href="https://app.thepackengers.com/admin/orders/${result.order_id}" target="_blank">${result.order_id}</a></td>
        <td>${result.photo_url ? `<img src="${result.photo_url}" alt="Photo" style="width: 100px; height: 100px;">` : 'N/A'}</td>
        <td style="max-width: 200px; word-wrap: break-word;">${result.description}</td>
        <td>
          <div class="dimensions-comparison vertical">
            <div class="dimensions-before">${ceilOrNA(result.current_dimensions.length)}×${ceilOrNA(result.current_dimensions.height)}×${ceilOrNA(result.current_dimensions.depth)} cm</div>
            <div class="dimensions-after ${aiDimsClass}">${ceilOrNA(result.ai_dimensions.length)}×${ceilOrNA(result.ai_dimensions.height)}×${ceilOrNA(result.ai_dimensions.depth)} cm</div>
          </div>
        </td>
        <td>
          <div class="comparison vertical">
            <div class="before">${result.nb_of_pieces || 'N/A'}</div>
            <div class="after ${aiNbClass}">${result.ai_nb_of_pieces || 'N/A'}</div>
          </div>
        </td>
        <td>
          <div class="comparison vertical">
            <div class="before">${result.category || 'N/A'}</div>
            <div class="after ${aiCatClass}">${result.ai_category || 'N/A'}</div>
          </div>
        </td>
        <td>
          <div class="comparison vertical">
            <div class="before">${currentTags || 'N/A'}</div>
            <div class="after ${aiTagsClass}">${aiTags || 'N/A'}</div>
          </div>
        </td>
        <td>
          <div class="comparison vertical">
            <div class="before">${formatVolume(result.current_volume)} m³</div>
            <div class="after ${aiDimsClass}">${formatVolume(result.ai_volume)} m³</div>
          </div>
        </td>
        <td>${formatVolume(result.packed_volume)} m³</td>
        <td>${formatVolume(result.real_volume)} m³</td>
        <td>${formatRatio(result.ratio_volume_prod)}</td>
        <td>${formatRatio(result.ratio_volume_local)}</td>
      </tr>
    `
    return rowHTML
  }

  getStatusText(status) {
    const statusMap = {
      'initializing': 'Initializing...',
      'processing': 'Processing...',
      'completed': 'Completed',
      'error': 'Error'
    }

    return statusMap[status] || 'Initializing...'
  }

  showError(message) {
    let displayMessage = message;
    if (typeof message === 'object') {
      if (message === null) {
        displayMessage = 'Unknown error';
      } else if (Array.isArray(message)) {
        displayMessage = message.join(', ');
      } else if (message.message) {
        displayMessage = message.message;
        if (message.stack) {
          displayMessage += '\n\nStack trace:\n' + message.stack;
        }
      } else {
        displayMessage = JSON.stringify(message);
      }
    }
    console.error('🛑 AI Comparison Error:', message);
    console.log('🛑 AI Comparison Error (displayed to user) :', displayMessage);
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = displayMessage;
      this.errorContainerTarget.style.display = 'block';
    }
  }

  hideError() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = ''
      this.errorContainerTarget.style.display = 'none'
    }
  }
} 